import GroupChatService from '../../services/GroupChatService';
import {
  ChatAttachment,
  GroupChatConversation,
  GroupChatParticipant,
} from '../../services/GroupChatService';
import {
  GroupChatConversationType,
  GroupChatMessageType,
} from '../../models/GroupChat';
import GroupChatHub from '../../services/GroupChatHub';
import { IChatMessage } from '../../components/GroupChat.vue';
import { AiQueryTarget } from '../../models/AiQueryTarget';
import {
  shouldClearPropipePendingState,
  shouldTrackPendingAiQuerySourceMessage,
} from './GroupChatAiQueryPolicy';

export interface ChatReaction {
  emoji: string;
  userIds: string[];
}

export interface ChatReaction {
  emoji: string;
  userIds: string[];
}

export interface GroupChatState {
  conversations: GroupChatConversation[];
  participantCandidates: GroupChatParticipant[];
  activeConversationId: number | null;
  unreadConversationIds: number[];
  messages: IChatMessage[];
  /** Reactions keyed by message id. */
  reactions: Record<number, ChatReaction[]>;
  loading: boolean;
  loadingOlder: boolean;
  sending: boolean;
  hasMoreMessages: boolean;
  groupName: string;
  someoneTyping: boolean;
  propipeQueryPending: boolean;
  pendingAiQuerySourceMessageId: number | null;
  accessVersion: number;
  hiddenMessageIds: number[];
  blockedUserIds: string[];
  chatSuspended: boolean;
}

let typingClearTimeout: ReturnType<typeof setTimeout> | null = null;

function canShowMessage(state: GroupChatState, message: IChatMessage): boolean {
  return (
    !state.chatSuspended &&
    !state.hiddenMessageIds.includes(message.id) &&
    !state.blockedUserIds.includes(message.userId) &&
    !state.hiddenMessageIds.includes(message.aiQuerySourceMessageId ?? 0)
  );
}

export default {
  state: {
    conversations: [] as GroupChatConversation[],
    participantCandidates: [] as GroupChatParticipant[],
    activeConversationId: null as number | null,
    unreadConversationIds: [] as number[],
    messages: [] as IChatMessage[],
    reactions: {} as Record<number, ChatReaction[]>,
    loading: false,
    loadingOlder: false,
    sending: false,
    hasMoreMessages: true,
    groupName: '',
    someoneTyping: false,
    propipeQueryPending: false,
    pendingAiQuerySourceMessageId: null,
    accessVersion: 0,
    hiddenMessageIds: [],
    blockedUserIds: [],
    chatSuspended: false,
  } as GroupChatState,

  mutations: {
    RESET_CHAT_ACCESS(state: GroupChatState) {
      state.accessVersion++;
      state.hiddenMessageIds = [];
      state.blockedUserIds = [];
      state.chatSuspended = false;
      state.messages = [];
      state.reactions = {};
      state.conversations = [];
      state.participantCandidates = [];
      state.activeConversationId = null;
      state.unreadConversationIds = [];
      state.someoneTyping = false;
      state.propipeQueryPending = false;
      state.pendingAiQuerySourceMessageId = null;
      state.loading = state.loadingOlder = state.sending = false;
      state.hasMoreMessages = true;
      state.groupName = '';
    },
    APPLY_CHAT_VISIBILITY(
      state: GroupChatState,
      payload: { requested: number[]; visible: number[]; suspended: boolean }
    ) {
      const removed = payload.requested.filter(
        (id) => !payload.visible.includes(id)
      );
      if (removed.length || state.chatSuspended !== payload.suspended)
        state.accessVersion++;
      state.chatSuspended = payload.suspended;
      state.hiddenMessageIds = [
        ...new Set([...state.hiddenMessageIds, ...removed]),
      ];
      state.messages = state.messages.filter((message) =>
        canShowMessage(state, message)
      );
      state.reactions = Object.fromEntries(
        Object.entries(state.reactions).filter(([id]) =>
          state.messages.some((message) => message.id === Number(id))
        )
      );
      if (removed.length || payload.suspended) {
        state.unreadConversationIds = [];
        state.someoneTyping = false;
        state.propipeQueryPending = false;
        state.pendingAiQuerySourceMessageId = null;
      }
      if (payload.suspended) {
        state.conversations = [];
        state.participantCandidates = [];
        state.activeConversationId = null;
      }
    },
    SET_PERSONAL_CHAT_BLOCK(
      state: GroupChatState,
      payload: { userId: string; blocked: boolean }
    ) {
      state.accessVersion++;
      state.blockedUserIds = state.blockedUserIds.filter(
        (id) => id !== payload.userId
      );
      if (payload.blocked) {
        state.blockedUserIds.push(payload.userId);
        state.hiddenMessageIds.push(
          ...state.messages
            .filter((message) => message.userId === payload.userId)
            .map((message) => message.id)
        );
        state.messages = state.messages.filter((message) =>
          canShowMessage(state, message)
        );
        state.participantCandidates = state.participantCandidates.filter(
          (user) => user.userId !== payload.userId
        );
        state.conversations = state.conversations.filter(
          (conversation) =>
            conversation.groupChatConversationType !==
              GroupChatConversationType.PrivateUsers ||
            conversation.participants.length > 2 ||
            !conversation.participants.some(
              (user) => user.userId === payload.userId
            )
        );
        for (const id of Object.keys(state.reactions)) {
          state.reactions[Number(id)] = state.reactions[Number(id)]
            .map((reaction) => ({
              ...reaction,
              userIds: reaction.userIds.filter(
                (userId) => userId !== payload.userId
              ),
            }))
            .filter((reaction) => reaction.userIds.length > 0);
        }
      }
      state.someoneTyping = false;
      state.unreadConversationIds = [];
    },
    SET_CHAT_CONVERSATIONS(
      state: GroupChatState,
      conversations: GroupChatConversation[]
    ) {
      const previousId = state.activeConversationId;
      state.conversations = conversations;
      if (!state.activeConversationId && conversations.length > 0) {
        state.activeConversationId = conversations[0].groupChatConversationId;
      } else if (
        state.activeConversationId &&
        !conversations.some(
          (conversation) =>
            conversation.groupChatConversationId === state.activeConversationId
        )
      ) {
        state.activeConversationId =
          conversations[0]?.groupChatConversationId ?? null;
      }
      if (previousId != null && previousId !== state.activeConversationId) {
        state.accessVersion++;
        state.messages = [];
        state.reactions = {};
        state.hasMoreMessages = true;
        state.someoneTyping = false;
        state.propipeQueryPending = false;
        state.pendingAiQuerySourceMessageId = null;
      }
    },
    SET_CHAT_PARTICIPANT_CANDIDATES(
      state: GroupChatState,
      candidates: GroupChatParticipant[]
    ) {
      state.participantCandidates = candidates;
    },
    SET_ACTIVE_CHAT_CONVERSATION(
      state: GroupChatState,
      conversationId: number | null
    ) {
      state.accessVersion++;
      state.activeConversationId = conversationId;
      state.unreadConversationIds = state.unreadConversationIds.filter(
        (id) => id !== conversationId
      );
      state.messages = [];
      state.reactions = {};
      state.hasMoreMessages = true;
      state.someoneTyping = false;
      state.propipeQueryPending = false;
      state.pendingAiQuerySourceMessageId = null;
    },
    ACCEPT_CHAT_HISTORY(state: GroupChatState, messages: IChatMessage[]) {
      const allowed = new Set(
        messages.flatMap((message) => [
          message.id,
          message.aiQuerySourceMessageId,
        ])
      );
      state.hiddenMessageIds = state.hiddenMessageIds.filter(
        (id) => !allowed.has(id)
      );
    },
    MERGE_CHAT_MESSAGES(state: GroupChatState, messages: IChatMessage[]) {
      const merged = new Map(
        state.messages.map((message) => [message.id, message])
      );
      for (const message of messages) merged.set(message.id, message);
      state.messages = [...merged.values()]
        .filter((message) => canShowMessage(state, message))
        .sort((left, right) => left.id - right.id);
    },
    SET_CHAT_MESSAGES(state: GroupChatState, messages: IChatMessage[]) {
      state.reactions = {};
      state.messages = messages.filter((message) =>
        canShowMessage(state, message)
      );
    },
    PREPEND_CHAT_MESSAGES(state: GroupChatState, messages: IChatMessage[]) {
      state.messages = [
        ...messages.filter(
          (message) =>
            canShowMessage(state, message) &&
            !state.messages.some((existing) => existing.id === message.id)
        ),
        ...state.messages,
      ];
    },
    APPEND_CHAT_MESSAGE(state: GroupChatState, message: IChatMessage) {
      if (!canShowMessage(state, message)) return;
      // Dedup — exact id match (e.g. sender gets POST response + SignalR broadcast).
      if (state.messages.some((m) => m.id === message.id)) return;

      // If this is a real server message, remove any optimistic placeholder
      // (negative temp id) from the same user with the same text.
      if (message.id > 0) {
        const optimisticIdx = state.messages.findIndex(
          (m) =>
            m.id < 0 && m.userId === message.userId && m.text === message.text
        );
        if (optimisticIdx !== -1) {
          state.messages.splice(optimisticIdx, 1);
        }
      }

      state.messages = [...state.messages, message];
    },
    MARK_CHAT_CONVERSATION_UNREAD(
      state: GroupChatState,
      conversationId: number
    ) {
      if (!state.unreadConversationIds.includes(conversationId)) {
        state.unreadConversationIds = [
          ...state.unreadConversationIds,
          conversationId,
        ];
      }
    },
    REMOVE_CHAT_MESSAGE(state: GroupChatState, messageId: number) {
      state.accessVersion++;
      state.hiddenMessageIds.push(messageId);
      state.messages = state.messages.filter((message) =>
        canShowMessage(state, message)
      );
      delete state.reactions[messageId];
    },
    DISCARD_OPTIMISTIC_MESSAGE(state: GroupChatState, tempId: number) {
      state.messages = state.messages.filter(
        (message) => message.id !== tempId
      );
    },
    REPLACE_OPTIMISTIC_MESSAGE(
      state: GroupChatState,
      payload: { tempId: number; real: IChatMessage }
    ) {
      const idx = state.messages.findIndex((m) => m.id === payload.tempId);
      if (idx !== -1) {
        state.messages[idx] = payload.real;
        state.messages = [...state.messages]; // trigger reactivity
      }
    },
    SET_CHAT_LOADING(state: GroupChatState, val: boolean) {
      state.loading = val;
    },
    SET_CHAT_LOADING_OLDER(state: GroupChatState, val: boolean) {
      state.loadingOlder = val;
    },
    SET_CHAT_SENDING(state: GroupChatState, val: boolean) {
      state.sending = val;
    },
    SET_CHAT_HAS_MORE(state: GroupChatState, val: boolean) {
      state.hasMoreMessages = val;
    },
    SET_GROUP_NAME(state: GroupChatState, name: string) {
      state.groupName = name;
    },
    SET_SOMEONE_TYPING(state: GroupChatState, val: boolean) {
      state.someoneTyping = val;
    },
    SET_PROPPIPE_QUERY_PENDING(state: GroupChatState, val: boolean) {
      state.propipeQueryPending = val;
      if (!val) {
        state.pendingAiQuerySourceMessageId = null;
      }
    },
    SET_PENDING_AI_QUERY_SOURCE_MESSAGE_ID(
      state: GroupChatState,
      messageId: number | null
    ) {
      state.pendingAiQuerySourceMessageId = messageId;
    },
    HYDRATE_REACTIONS(state: GroupChatState, messages: any[]) {
      for (const msg of messages) {
        if (
          canShowMessage(state, msg) &&
          msg.reactions &&
          msg.reactions.length > 0
        ) {
          state.reactions[msg.id] = msg.reactions
            .map((r: any) => ({
              emoji: r.emoji,
              userIds: r.userIds.filter(
                (userId: string) => !state.blockedUserIds.includes(userId)
              ),
            }))
            .filter((reaction: ChatReaction) => reaction.userIds.length > 0);
        }
      }
      state.reactions = { ...state.reactions };
    },
    APPLY_REACTION(
      state: GroupChatState,
      payload: {
        messageId: number;
        userId: string;
        emoji: string;
        added: boolean;
      }
    ) {
      const { messageId, userId, emoji, added } = payload;
      if (
        state.chatSuspended ||
        state.blockedUserIds.includes(userId) ||
        !state.messages.some((message) => message.id === messageId)
      )
        return;
      if (!state.reactions[messageId]) {
        state.reactions[messageId] = [];
      }
      const reactions = state.reactions[messageId];
      let entry = reactions.find((r) => r.emoji === emoji);

      if (added) {
        if (!entry) {
          entry = { emoji, userIds: [] };
          reactions.push(entry);
        }
        if (!entry.userIds.includes(userId)) {
          entry.userIds.push(userId);
        }
      } else if (entry) {
        entry.userIds = entry.userIds.filter((u) => u !== userId);
        if (entry.userIds.length === 0) {
          state.reactions[messageId] = reactions.filter(
            (r) => r.emoji !== emoji
          );
        }
      }
      // Trigger reactivity
      state.reactions = { ...state.reactions };
    },
    SET_MESSAGE_REACTIONS(
      state: GroupChatState,
      payload: { messageId: number; reactions: ChatReaction[] }
    ) {
      state.reactions[payload.messageId] = payload.reactions.map((r) => ({
        emoji: r.emoji,
        userIds: [...r.userIds],
      }));
      state.reactions = { ...state.reactions };
    },
  },

  actions: {
    async reconcileChatVisibility(context: any) {
      const userId = context.rootState.user?.id;
      if (!userId) return;
      const messageIds = context.state.messages
        .map((message: IChatMessage) => message.id)
        .filter((id: number) => id > 0);
      for (
        let offset = 0;
        offset < Math.max(1, messageIds.length);
        offset += 200
      ) {
        const requested = messageIds.slice(offset, offset + 200);
        const version = context.state.accessVersion;
        const result = await GroupChatService.getVisibility(requested);
        if (
          userId !== context.rootState.user?.id ||
          version !== context.state.accessVersion
        )
          return;
        context.commit('APPLY_CHAT_VISIBILITY', {
          requested,
          visible: result.visibleMessageIds,
          suspended: result.isChatSuspended || !result.groupId,
        });
        if (result.isChatSuspended || !result.groupId) return;
      }
    },

    async setPersonalChatBlock(
      context: any,
      payload: { userId: string; blocked: boolean }
    ) {
      const sessionUserId = context.rootState.user?.id;
      await GroupChatService.setBlocked(payload.userId, payload.blocked);
      if (sessionUserId !== context.rootState.user?.id) return;
      context.commit('SET_PERSONAL_CHAT_BLOCK', payload);
      if (
        !context.state.conversations.some(
          (conversation: GroupChatConversation) =>
            conversation.groupChatConversationId ===
            context.state.activeConversationId
        )
      ) {
        context.commit('SET_ACTIVE_CHAT_CONVERSATION', null);
      }
    },

    async fetchChatConversations(context: any) {
      const version = context.state.accessVersion;
      try {
        const data = await GroupChatService.getConversations();
        if (
          version !== context.state.accessVersion ||
          context.state.chatSuspended
        )
          return;
        context.commit('SET_CHAT_CONVERSATIONS', data.conversations);
        context.commit('SET_GROUP_NAME', data.groupName);
      } catch (e) {
        console.error('[GroupChatStore] Failed to load conversations:', e);
      }
    },

    async fetchChatParticipantCandidates(context: any) {
      const version = context.state.accessVersion;
      try {
        const candidates = await GroupChatService.getParticipantCandidates();
        if (version !== context.state.accessVersion) return;
        context.commit('SET_CHAT_PARTICIPANT_CANDIDATES', candidates);
      } catch (e) {
        console.error(
          '[GroupChatStore] Failed to load participant candidates:',
          e
        );
      }
    },

    async setActiveChatConversation(context: any, conversationId: number) {
      context.commit('SET_ACTIVE_CHAT_CONVERSATION', conversationId);
      await context.dispatch('fetchChatMessages');
    },

    async createChatConversation(
      context: any,
      payload: {
        participantUserIds?: string[];
        title?: string | null;
        isPropipeAi?: boolean;
      }
    ) {
      const conversation = await GroupChatService.createConversation(payload);
      const data = await GroupChatService.getConversations();
      context.commit('SET_CHAT_CONVERSATIONS', data.conversations);
      context.commit('SET_GROUP_NAME', data.groupName);
      await context.dispatch(
        'setActiveChatConversation',
        conversation.groupChatConversationId
      );
      return conversation;
    },

    async chatConversationCreated(
      context: any,
      payload: { groupChatConversationId?: number | null }
    ) {
      if (!payload.groupChatConversationId) {
        await context.dispatch('fetchChatConversations');
        return;
      }

      const hasConversation = context.state.conversations.some(
        (conversation: GroupChatConversation) =>
          conversation.groupChatConversationId ===
          payload.groupChatConversationId
      );
      if (!hasConversation) {
        await context.dispatch('fetchChatConversations');
        context.commit(
          'MARK_CHAT_CONVERSATION_UNREAD',
          payload.groupChatConversationId
        );
      }
    },

    async leaveChatConversation(context: any, conversationId: number) {
      await GroupChatService.leaveConversation(conversationId);
      const conversations = context.state.conversations.filter(
        (conversation: GroupChatConversation) =>
          conversation.groupChatConversationId !== conversationId
      );
      context.commit('SET_CHAT_CONVERSATIONS', conversations);
      await context.dispatch('fetchChatMessages');
    },

    async fetchChatMessages(context: any, merge = false) {
      if (context.state.chatSuspended) return;
      const version = context.state.accessVersion;
      context.commit('SET_CHAT_LOADING', true);
      try {
        const data = await GroupChatService.getMessages(
          undefined,
          context.state.activeConversationId
        );
        if (version !== context.state.accessVersion) return;
        context.commit('ACCEPT_CHAT_HISTORY', data.messages);
        context.commit(
          merge ? 'MERGE_CHAT_MESSAGES' : 'SET_CHAT_MESSAGES',
          data.messages
        );
        context.commit('HYDRATE_REACTIONS', data.messages);
        if (!merge)
          context.commit('SET_CHAT_HAS_MORE', data.messages.length >= 20);
        context.commit('SET_GROUP_NAME', data.groupName);
      } catch (e) {
        console.error('[GroupChatStore] Failed to load messages:', e);
      } finally {
        context.commit('SET_CHAT_LOADING', false);
      }
    },

    async loadOlderChatMessages(context: any) {
      const state: GroupChatState = context.state;
      if (
        state.loadingOlder ||
        !state.hasMoreMessages ||
        state.messages.length === 0
      )
        return;

      const oldestId = state.messages[0].id;
      const version = state.accessVersion;
      context.commit('SET_CHAT_LOADING_OLDER', true);
      try {
        const data = await GroupChatService.getMessages(
          oldestId,
          state.activeConversationId
        );
        if (version !== state.accessVersion) return;
        context.commit('SET_CHAT_HAS_MORE', data.messages.length >= 20);
        context.commit('ACCEPT_CHAT_HISTORY', data.messages);
        context.commit('PREPEND_CHAT_MESSAGES', data.messages);
        context.commit('HYDRATE_REACTIONS', data.messages);
      } catch (e) {
        console.error('[GroupChatStore] Failed to load older messages:', e);
      } finally {
        context.commit('SET_CHAT_LOADING_OLDER', false);
      }
    },

    async sendChatMessage(
      context: any,
      payload:
        | string
        | {
            text: string;
            attachment?: ChatAttachment | null;
            aiQueryTarget?: AiQueryTarget | null;
          }
    ) {
      // Support both old (string) and new (object) call signatures.
      const text = typeof payload === 'string' ? payload : payload.text;
      const attachment =
        typeof payload === 'string' ? null : payload.attachment;
      const aiQueryTarget =
        typeof payload === 'string' ? null : payload.aiQueryTarget ?? null;
      const activeConversation = context.state.conversations.find(
        (conversation: GroupChatConversation) =>
          conversation.groupChatConversationId ===
          context.state.activeConversationId
      );
      const effectiveAiQueryTarget =
        aiQueryTarget ??
        (activeConversation?.groupChatConversationType ===
        GroupChatConversationType.PropipeAi
          ? AiQueryTarget.Propipe
          : null);

      if (context.state.sending || context.state.chatSuspended)
        throw new Error('Chat is unavailable.');
      const version = context.state.accessVersion;
      const conversationId = context.state.activeConversationId;
      const user = context.rootState.user;
      const tempId = -Date.now();
      context.commit('APPEND_CHAT_MESSAGE', {
        id: tempId,
        groupChatConversationId: conversationId,
        userId: user?.id ?? '',
        userName: user?.name ?? 'You',
        text,
        timestamp: new Date().toISOString(),
        attachment: attachment ?? null,
        aiQuerySourceMessageId: null,
      });
      try {
        const saved = await GroupChatService.postMessage(
          text,
          attachment,
          effectiveAiQueryTarget,
          conversationId
        );
        if (version !== context.state.accessVersion) return saved;
        context.commit('REPLACE_OPTIMISTIC_MESSAGE', {
          tempId,
          real: saved,
        });
        if (shouldTrackPendingAiQuerySourceMessage(effectiveAiQueryTarget)) {
          context.commit('SET_PROPPIPE_QUERY_PENDING', true);
          context.commit('SET_PENDING_AI_QUERY_SOURCE_MESSAGE_ID', saved.id);
        }
        return saved;
      } catch (error) {
        context.commit('DISCARD_OPTIMISTIC_MESSAGE', tempId);
        throw error;
      }
    },

    async deleteChatMessage(context: any, id: number) {
      const version = context.state.accessVersion;
      await GroupChatService.deleteMessage(id);
      if (version !== context.state.accessVersion) return;
      context.commit('REMOVE_CHAT_MESSAGE', id);
    },

    // Dispatched by Hubs.ts when a ReceiveChatMessage SignalR event fires.
    async chatMessageReceived(context: any, msg: IChatMessage) {
      if (!canShowMessage(context.state, msg)) return;
      const activeConversationId = context.state.activeConversationId;
      if (
        msg.groupChatConversationId &&
        activeConversationId &&
        msg.groupChatConversationId !== activeConversationId
      ) {
        const hasConversation = context.state.conversations.some(
          (conversation: GroupChatConversation) =>
            conversation.groupChatConversationId === msg.groupChatConversationId
        );
        if (
          !hasConversation ||
          msg.messageType === GroupChatMessageType.UserLeft
        ) {
          await context.dispatch('fetchChatConversations');
        }
        context.commit(
          'MARK_CHAT_CONVERSATION_UNREAD',
          msg.groupChatConversationId
        );
        return;
      }

      context.commit('APPEND_CHAT_MESSAGE', msg);
      if (msg.messageType === GroupChatMessageType.UserLeft) {
        await context.dispatch('fetchChatConversations');
      }

      // Clear the "thinking" indicator when the bot responds.
      if (
        shouldClearPropipePendingState(
          msg.userId,
          msg.aiQuerySourceMessageId,
          context.state.pendingAiQuerySourceMessageId
        )
      ) {
        context.commit('SET_PROPPIPE_QUERY_PENDING', false);
      }
    },

    // Dispatched by Hubs.ts when a ReceiveChatMessageDeleted SignalR event fires.
    chatMessageDeleted(
      context: any,
      payload: { messageId: number; groupChatConversationId?: number | null }
    ) {
      const activeConversationId = context.state.activeConversationId;
      if (
        payload.groupChatConversationId &&
        activeConversationId &&
        payload.groupChatConversationId !== activeConversationId
      ) {
        return;
      }

      context.commit('REMOVE_CHAT_MESSAGE', payload.messageId);
    },

    async toggleReaction(
      context: any,
      payload: { messageId: number; emoji: string }
    ) {
      const userId = context.rootState.user?.id ?? '';
      const reactions = context.state.reactions[payload.messageId] || [];
      const existing = reactions.find(
        (r: ChatReaction) => r.emoji === payload.emoji
      );
      const alreadyReacted = existing?.userIds.includes(userId);
      const optimisticAdded = !alreadyReacted;

      // Optimistic update — gives instant feedback.
      context.commit('APPLY_REACTION', {
        ...payload,
        userId,
        added: optimisticAdded,
      });

      try {
        const result = await GroupChatService.toggleReaction(
          payload.messageId,
          payload.emoji
        );

        // Reconcile only if the server disagrees with our optimistic guess.
        if (result.added !== optimisticAdded) {
          context.commit('APPLY_REACTION', {
            ...payload,
            userId,
            added: result.added,
          });
        }
      } catch (e) {
        console.error('[GroupChatStore] Failed to toggle reaction:', e);
        // Revert optimistic update — restore original state before the click.
        context.commit('APPLY_REACTION', {
          ...payload,
          userId,
          added: !!alreadyReacted,
        });
      }
    },

    // Dispatched by Hubs.ts when a ReceiveReactionUpdate SignalR event fires.
    // Skip reactions from the current user — those are already handled by the
    // optimistic update + API reconciliation path in toggleReaction. Processing
    // the SignalR echo would cause redundant APPLY_REACTION mutations that
    // interfere with in-flight toggle sequences.
    reactionReceived(
      context: any,
      payload: {
        messageId: number;
        groupChatConversationId?: number | null;
        userId: string;
        emoji: string;
        added: boolean;
      }
    ) {
      const activeConversationId = context.state.activeConversationId;
      if (
        payload.groupChatConversationId &&
        activeConversationId &&
        payload.groupChatConversationId !== activeConversationId
      ) {
        return;
      }

      const currentUserId = context.rootState.user?.id ?? '';
      if (payload.userId === currentUserId) return;

      context.commit('APPLY_REACTION', payload);
    },

    // Dispatched by Hubs.ts when a ReceiveTypingIndicator event fires.
    chatTypingReceived(
      context: any,
      payload: { groupChatConversationId?: number | null }
    ) {
      if (
        context.state.chatSuspended ||
        context.state.blockedUserIds.length > 0
      )
        return;
      const activeConversationId = context.state.activeConversationId;
      if (
        payload.groupChatConversationId &&
        activeConversationId &&
        payload.groupChatConversationId !== activeConversationId
      ) {
        return;
      }

      context.commit('SET_SOMEONE_TYPING', true);
      if (typingClearTimeout) clearTimeout(typingClearTimeout);
      typingClearTimeout = setTimeout(() => {
        context.commit('SET_SOMEONE_TYPING', false);
        typingClearTimeout = null;
      }, 3000);
    },

    sendChatTyping(context: any) {
      if (context.state.chatSuspended) return;
      GroupChatHub.sendTyping(context.state.activeConversationId);
    },
  },
};
